import { MapSettings, PosterTextData, MapRoute, MapIcon } from '@/types/canvas';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { MapTextElements } from './MapTextElements';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { buildMapboxStyle, getColorsFromSettings } from '@/config/mapboxStyles';
import { Lock, Unlock } from 'lucide-react';
import { getIconColor } from '@/utils/iconUtils';
import { iconDataCache } from '@/utils/iconDataCache';
import { svgIconCache, updateSvgFill, sanitizeSvg } from '@/utils/svgIconCache';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/utils/deviceDetection';

interface MapCanvasProps {
  settings: MapSettings;
  width: number;
  height: number;
  mapIcons?: MapIcon[];
  selectedIconId?: string | null; // ID vybrané ikony pro vkládání
  selectedMapIconId?: string | null; // ID vybrané ikony na mapě pro editaci
  selectedIconColor?: string; // Barva pro nově vkládané ikony (pro kurzor)
  mapScale?: number; // Scale faktor mapy pro kompenzaci velikosti kurzoru
  onMapChange?: (centerLat: number, centerLng: number, zoom: number, bearing?: number) => void;
  onMapClick?: (lat: number, lng: number) => void;
  onIconClick?: (iconId: string) => void; // Callback při kliknutí na ikonu na mapě
  onIconUpdate?: (iconId: string, lat: number, lng: number) => void; // Callback při přesunu ikony
  onIconSelectCancel?: () => void; // Callback pro zrušení výběru ikony (ESC)
  onMapInstanceReady?: (mapInstance: mapboxgl.Map | null) => void;
  textElements?: any[]; // TextElement[] z MapStylePreset
  textData?: PosterTextData;
  routes?: MapRoute[]; // jednoduché uživatelské trasy
  needsExport?: boolean; // Zda je potřeba preserveDrawingBuffer pro export (default: false)
}

export function MapCanvas({ settings, width, height, mapIcons = [], selectedIconId = null, selectedMapIconId = null, selectedIconColor = '#000000', mapScale = 1, onMapChange, onMapClick, onIconClick, onIconUpdate, onIconSelectCancel, onMapInstanceReady, textElements = [], textData, routes = [], needsExport = false }: MapCanvasProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const routesRef = useRef<MapRoute[]>(routes);
  const prevDimensionsRef = useRef<{ width: number; height: number } | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const markersMapRef = useRef<Map<string, mapboxgl.Marker>>(new Map()); // Mapování markerů podle ID ikony
  const selectedIconDataRef = useRef<any>(null);
  const globalCursorElementRef = useRef<HTMLDivElement | null>(null);
  const [isMapLocked, setIsMapLocked] = useState(false);

  // držíme poslední hodnotu routes pro reattach po změně stylu
  useEffect(() => {
    routesRef.current = routes;
  }, [routes]);


  const addOrUpdateRoutes = (m: mapboxgl.Map, rts: MapRoute[]) => {
    // odstranění starých vrstev i zdrojů, které patří do naší domény
    const existingStyle = m.getStyle();
    const existingLayerIds = (existingStyle?.layers || []).map(l => l.id);
    const existingSourceIds = Object.keys(existingStyle?.sources || {});

    const ourLayerPrefix = 'route-';
    const ourPointPrefix = 'route-point-';
    const ourSourcePrefix = 'route-src-';

    existingLayerIds.forEach(id => {
      if (id.startsWith(ourLayerPrefix) || id.startsWith(ourPointPrefix)) {
        try { m.removeLayer(id); } catch { }
      }
    });
    existingSourceIds.forEach(id => {
      if (id.startsWith(ourSourcePrefix)) {
        try { m.removeSource(id); } catch { }
      }
    });

    // přidej nové
    rts.forEach(route => {
      if (!route.coordinates || route.coordinates.length < 2) return;
      const srcId = `${ourSourcePrefix}${route.id}`;
      const lineId = `${ourLayerPrefix}${route.id}`;
      const startId = `${ourPointPrefix}${route.id}-start`;
      const endId = `${ourPointPrefix}${route.id}-end`;

      const lineGeoJson = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: route.coordinates,
            },
          },
        ],
      } as const;

      m.addSource(srcId, {
        type: 'geojson',
        data: lineGeoJson as any,
      });

      m.addLayer({
        id: lineId,
        type: 'line',
        source: srcId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': route.color || '#D81B60',
          'line-width': route.widthPx ?? 3,
          'line-opacity': route.opacity ?? 1,
          ...(route.dashArray ? { 'line-dasharray': route.dashArray as any } : {}),
        },
      });

      if (route.showStartEnd !== false) {
        const [startLng, startLat] = route.coordinates[0];
        const [endLng, endLat] = route.coordinates[route.coordinates.length - 1];

        const startSrcId = `${srcId}-start`;
        const endSrcId = `${srcId}-end`;

        m.addSource(startSrcId, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [
              { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [startLng, startLat] } },
            ],
          } as any,
        });
        m.addSource(endSrcId, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [
              { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [endLng, endLat] } },
            ],
          } as any,
        });

        m.addLayer({
          id: startId,
          type: 'circle',
          source: startSrcId,
          paint: {
            'circle-radius': 4,
            'circle-color': route.startMarkerColor || '#2E7D32',
            'circle-stroke-color': '#FFFFFF',
            'circle-stroke-width': 1,
          },
        });
        m.addLayer({
          id: endId,
          type: 'circle',
          source: endSrcId,
          paint: {
            'circle-radius': 4,
            'circle-color': route.endMarkerColor || '#C62828',
            'circle-stroke-color': '#FFFFFF',
            'circle-stroke-width': 1,
          },
        });
      }
    });
  };

  // Refs for callbacks to avoid stale closures in Mapbox listeners
  const onMapClickRef = useRef(onMapClick);
  const onMapChangeRef = useRef(onMapChange);
  const onIconClickRef = useRef(onIconClick);
  const onIconUpdateRef = useRef(onIconUpdate);

  useEffect(() => {
    onMapClickRef.current = onMapClick;
    onMapChangeRef.current = onMapChange;
    onIconClickRef.current = onIconClick;
    onIconUpdateRef.current = onIconUpdate;
  }, [onMapClick, onMapChange, onIconClick, onIconUpdate]);

  // Inicializace mapy
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Load Mapbox token from environment (no fallback)
    const token = import.meta.env.VITE_MAPBOX_TOKEN;
    if (!token) {
      throw new Error(
        'Missing VITE_MAPBOX_TOKEN!\n\n' +
        'Get your token from: https://account.mapbox.com/access-tokens/\n' +
        'Add it to frontend/.env file\n' +
        'See frontend/ENVIRONMENT_SETUP.md for details.'
      );
    }

    mapboxgl.accessToken = token;

    // Zkontrolovat WebGL podporu před inicializací (iOS Opera může mít problémy)
    if (!mapboxgl.supported()) {
      console.error('Mapbox GL is not supported in this browser');
      setIsInitialized(false);
      if (onMapInstanceReady) {
        onMapInstanceReady(null);
      }
      return;
    }

    // Použít memoizovaný customStyle pro inicializaci
    const colors = getColorsFromSettings(settings);
    const showLabels = settings.showLabels !== false;
    const showBuildings = settings.showBuildings !== false;
    const initialStyle = buildMapboxStyle(
      colors,
      showLabels,
      showBuildings,
      settings.mapLayer,
      settings.zoom,
      settings.detailLevel || 'standard',
      settings.showMinorRoads !== false,
      settings.showPaths !== false,
      settings.showPedestrian !== false,
      settings.customZoomThresholds
    );

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: initialStyle as any,
        center: [settings.centerLng, settings.centerLat],
        zoom: settings.zoom,
        bearing: settings.bearing ?? 0,
        preserveDrawingBuffer: needsExport && !isMobile, // Pouze pokud je potřeba export a není mobil
        attributionControl: false,
        maxBounds: undefined,
        // Antialiasing vypnout na mobilu pro úsporu paměti
        antialias: !isMobile,
      });

      // Event handlers - ALWAYS use refs to get latest callback
      map.current.on('moveend', () => {
        if (!map.current) return;
        if (onMapChangeRef.current) {
          const center = map.current.getCenter();
          const zoom = map.current.getZoom();
          const bearing = map.current.getBearing();
          onMapChangeRef.current(center.lat, center.lng, zoom, bearing);
        }
      });

      map.current.on('zoomend', () => {
        if (!map.current) return;
        if (onMapChangeRef.current) {
          const center = map.current.getCenter();
          const zoom = map.current.getZoom();
          const bearing = map.current.getBearing();
          onMapChangeRef.current(center.lat, center.lng, zoom, bearing);
        }
      });

      map.current.on('click', (e) => {
        if (onMapClickRef.current) {
          const { lng, lat } = e.lngLat;
          onMapClickRef.current(lat, lng);
        }
      });

      // Initial map lock state
      if (isMapLocked) {
        map.current.dragPan.disable();
        map.current.dragRotate.disable();
        map.current.scrollZoom.disable();
        map.current.boxZoom.disable();
        map.current.doubleClickZoom.disable();
        map.current.touchZoomRotate.disable();
      }

      map.current.on('load', () => {
        setIsInitialized(true);
        if (onMapInstanceReady) {
          onMapInstanceReady(map.current);
        }
        // Inicializovat předchozí rozměry při prvním načtení
        prevDimensionsRef.current = { width, height };

        // Zajistit, že se mapa správně vykreslí a vyplní kontejner
        setTimeout(() => {
          if (map.current) {
            map.current.resize();

            // Vynutit překreslení canvas s přesnými rozměry a vysokým rozlišením
            const canvas = map.current.getCanvas();
            if (canvas) {
              // Nastavit CSS rozměry (zobrazované)
              canvas.style.width = `${width}px`;
              canvas.style.height = `${height}px`;
              canvas.style.display = 'block';
              canvas.style.position = 'absolute';
              canvas.style.top = '0';
              canvas.style.left = '0';
              canvas.style.right = '0';
              canvas.style.bottom = '0';
              canvas.style.objectFit = 'cover';
              // Zajistit vysokou kvalitu renderování
              canvas.style.imageRendering = 'high-quality';
            }

            // Vynutit překreslení celé mapy
            map.current.triggerRepaint();

            // po loadu přidat případné trasy
            try {
              addOrUpdateRoutes(map.current, routesRef.current);
            } catch (e) {
              console.warn('Route layers init failed:', e);
            }
          }
        }, 100);
      });
    } catch (error) {
      console.error('Chyba při inicializaci Mapbox mapy:', error);
      setIsInitialized(false);
      if (onMapInstanceReady) {
        onMapInstanceReady(null);
      }
      // Nevyhazovat chybu, aby aplikace mohla pokračovat
    }

    return () => {
      try {
        if (map.current) {
          map.current.remove();
          map.current = null;
        }
      } catch (error) {
        console.error('Error cleaning up map:', error);
      }
    };
  }, []); // Pouze při mount

  // Resize mapa pouze když se změní logické rozměry (ne při změně viewportu)
  // Mapbox má fixní rozměry, které se nemění při resize okna
  useEffect(() => {
    if (map.current && isInitialized) {
      // Zkontrolovat, zda se skutečně změnily rozměry
      const prevDims = prevDimensionsRef.current;
      const dimensionsChanged = !prevDims || prevDims.width !== width || prevDims.height !== height;

      if (dimensionsChanged) {
        prevDimensionsRef.current = { width, height };

        // Malé zpoždění pro zajištění, že DOM je aktualizován
        setTimeout(() => {
          if (map.current) {
            // Vynutit resize mapy pouze pokud se změnily logické rozměry
            map.current.resize();

            // Vynutit překreslení canvas s přesnými rozměry a vysokým rozlišením
            const canvas = map.current.getCanvas();
            if (canvas) {
              // Nastavit CSS rozměry (zobrazované)
              canvas.style.width = `${width}px`;
              canvas.style.height = `${height}px`;
              canvas.style.display = 'block';
              canvas.style.position = 'absolute';
              canvas.style.top = '0';
              canvas.style.left = '0';
              canvas.style.right = '0';
              canvas.style.bottom = '0';
              canvas.style.objectFit = 'cover';
              // Zajistit vysokou kvalitu renderování
              canvas.style.imageRendering = 'high-quality';
            }

            // Vynutit překreslení celé mapy
            map.current.triggerRepaint();

            // Zajistit, že se mapa správně vykreslí po resize
            setTimeout(() => {
              if (map.current) {
                map.current.resize();
                map.current.triggerRepaint();
              }
            }, 100);
          }
        }, 50);
      }
    }
  }, [width, height, isInitialized]);

  // Memoizovat customStyle pro optimalizaci - přepočítá se pouze při změně relevantních settings
  const customStyle = useMemo(() => {
    const colors = getColorsFromSettings(settings);
    const showLabels = settings.showLabels !== false;
    const showBuildings = settings.showBuildings !== false;

    return buildMapboxStyle(
      colors,
      showLabels,
      showBuildings,
      settings.mapLayer,
      settings.zoom,
      settings.detailLevel || 'standard',
      settings.showMinorRoads !== false,
      settings.showPaths !== false,
      settings.showPedestrian !== false,
      settings.customZoomThresholds
    );
  }, [
    settings.colorScheme,
    settings.colors,
    settings.customColors,
    settings.showLabels,
    settings.showBuildings,
    settings.mapLayer,
    settings.zoom,
    settings.detailLevel,
    settings.showMinorRoads,
    settings.showPaths,
    settings.showPedestrian,
    settings.customZoomThresholds,
  ]);

  // Update stylu při změně nastavení - použít memoizovaný customStyle
  useEffect(() => {
    if (!map.current || !isInitialized) return;

    try {
      // Počkat až bude styl připravený pro změnu
      if (map.current.isStyleLoaded()) {
        map.current.setStyle(customStyle as any);
      } else {
        // Pokud styl není načtený, počkat na load event
        const handleStyleLoad = () => {
          if (map.current) {
            map.current.setStyle(customStyle as any);
            map.current.off('styledata', handleStyleLoad);
          }
        };
        map.current.on('styledata', handleStyleLoad);
      }
    } catch (error) {
      console.error('Chyba při aktualizaci stylu mapy:', error);
    }
  }, [customStyle, isInitialized]);

  // Sloučený useEffect pro trasy - přidá trasy po změně stylu i při změně routes props
  useEffect(() => {
    if (!map.current || !isInitialized) return;

    const addRoutesWhenReady = () => {
      if (map.current && map.current.isStyleLoaded()) {
        try {
          addOrUpdateRoutes(map.current, routes);
        } catch (e) {
          console.warn('Route layers update failed:', e);
        }
      } else {
        // Pokud styl není načtený, počkat na styledata event
        if (map.current) {
          const handler = () => {
            if (map.current && map.current.isStyleLoaded()) {
              try {
                addOrUpdateRoutes(map.current, routes);
              } catch (e) {
                console.warn('Route layers update failed:', e);
              }
              map.current.off('styledata', handler);
            }
          };
          map.current.on('styledata', handler);
        }
      }
    };

    // Přidat trasy při změně routes
    addRoutesWhenReady();

    // Také přidat trasy po změně stylu (styledata event)
    const styleChangeHandler = () => {
      if (map.current) {
        try {
          addOrUpdateRoutes(map.current, routes);
        } catch (e) {
          console.warn('Route layers reattached after style load failed:', e);
        }
      }
    };

    map.current.on('styledata', styleChangeHandler);

    return () => {
      map.current?.off('styledata', styleChangeHandler);
    };
  }, [routes, isInitialized]);

  // Update pozice, zoomu a rotace
  useEffect(() => {
    if (!map.current || !isInitialized) return;

    const currentCenter = map.current.getCenter();
    const currentZoom = map.current.getZoom();
    const currentBearing = map.current.getBearing();
    const bearing = settings.bearing ?? 0;

    // Pouze pokud se změnila pozice/zoom/bearing (zamezí nekonečné smyčce)
    const centerChanged =
      Math.abs(currentCenter.lat - settings.centerLat) > 0.0001 ||
      Math.abs(currentCenter.lng - settings.centerLng) > 0.0001;
    const zoomChanged = Math.abs(currentZoom - settings.zoom) > 0.01;
    const bearingChanged = Math.abs(currentBearing - bearing) > 0.5;

    if (centerChanged || zoomChanged || bearingChanged) {
      map.current.jumpTo({
        center: [settings.centerLng, settings.centerLat],
        zoom: settings.zoom,
        bearing,
      });
    }
  }, [settings.centerLat, settings.centerLng, settings.zoom, settings.bearing, isInitialized]);

  // Handle map lock/unlock
  useEffect(() => {
    if (!map.current || !isInitialized) return;

    if (isMapLocked) {
      map.current.dragPan.disable();
      map.current.dragRotate.disable();
      map.current.scrollZoom.disable();
      map.current.boxZoom.disable();
      map.current.doubleClickZoom.disable();
      map.current.touchZoomRotate.disable();
    } else {
      // Only enable if icon is not selected (icon mode disables drag/pan)
      if (!selectedIconId) {
        map.current.dragPan.enable();
        map.current.dragRotate.enable();
        map.current.scrollZoom.enable();
        map.current.boxZoom.enable();
        map.current.doubleClickZoom.enable();
        map.current.touchZoomRotate.enable();
      }
    }
  }, [isMapLocked, isInitialized, selectedIconId]);

  // Handle selected icon for insertion mode
  useEffect(() => {
    if (!map.current || !isInitialized) return;

    const updateIconMode = async () => {
      if (!selectedIconId) {
        // Reset mode - enable drag/pan if not locked, remove custom cursor
        if (!isMapLocked) {
          map.current?.dragPan.enable();
          map.current?.dragRotate.enable();
          map.current?.scrollZoom.enable();
          map.current?.boxZoom.enable();
          map.current?.doubleClickZoom.enable();
          map.current?.touchZoomRotate.enable();
        }
        const container = map.current?.getCanvasContainer();
        if (container) {
          container.style.cursor = '';
        }
        // Remove global cursor
        if (globalCursorElementRef.current) {
          const cleanup = (globalCursorElementRef.current as any).__cleanup;
          if (cleanup) cleanup();
          globalCursorElementRef.current.remove();
          globalCursorElementRef.current = null;
        }
        document.body.style.cursor = '';
        selectedIconDataRef.current = null;
        return;
      }

      // Load icon data using shared cache
      const iconData = await iconDataCache.get(selectedIconId);
      if (!iconData) {
        console.warn(`Failed to load selected icon ${selectedIconId}`);
        return;
      }

      selectedIconDataRef.current = iconData;

      // Disable drag/pan when inserting icon (regardless of lock state)
      map.current?.dragPan.disable();
      map.current?.dragRotate.disable();
      map.current?.scrollZoom.disable();
      map.current?.boxZoom.disable();
      map.current?.doubleClickZoom.disable();
      map.current?.touchZoomRotate.disable();

      // Hide default cursor
      document.body.style.cursor = 'none';
      const container = map.current?.getCanvasContainer();
      if (container) {
        container.style.cursor = 'none';
      }

      // Create global cursor element (visible everywhere)
      if (globalCursorElementRef.current) {
        globalCursorElementRef.current.remove();
      }

      const globalCursorEl = document.createElement('div');
      globalCursorEl.style.position = 'fixed';
      globalCursorEl.style.pointerEvents = 'none';
      globalCursorEl.style.zIndex = '99999';
      // Kompenzovat velikost kurzoru podle scale faktoru mapy
      const cursorWidth = (iconData.default_width || 40) * mapScale;
      const cursorHeight = (iconData.default_height || 40) * mapScale;
      globalCursorEl.style.width = `${cursorWidth}px`;
      globalCursorEl.style.height = `${cursorHeight}px`;
      globalCursorEl.style.display = 'none';
      globalCursorEl.style.transform = 'translate(-50%, -50%)';

      // Načíst SVG a upravit fill atribut pro kurzor
      const svgContent = await svgIconCache.getSvg(iconData.storage_url);
      if (svgContent) {
        const coloredSvg = updateSvgFill(svgContent, selectedIconColor);
        const sanitizedSvg = sanitizeSvg(coloredSvg);

        // Použít DOMParser pro správné parsování SVG
        try {
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(sanitizedSvg, 'image/svg+xml');

          const parserError = svgDoc.querySelector('parsererror');
          if (parserError) {
            console.warn('[MapCanvas] SVG parsing error for cursor:', parserError.textContent);
          } else {
            const svgElement = svgDoc.querySelector('svg') as SVGSVGElement;
            if (svgElement) {
              svgElement.style.width = '100%';
              svgElement.style.height = '100%';
              svgElement.style.display = 'block';
              svgElement.style.opacity = '0.8';
              svgElement.style.margin = '0';
              svgElement.style.padding = '0';
              svgElement.style.verticalAlign = 'middle';
              if (iconData.name) {
                svgElement.setAttribute('aria-label', iconData.name);
              }
              globalCursorEl.appendChild(svgElement);
            }
          }
        } catch (err) {
          console.error('[MapCanvas] Error parsing SVG for cursor:', err);
        }
      }

      document.body.appendChild(globalCursorEl);
      globalCursorElementRef.current = globalCursorEl;

      // Update global cursor position on mouse move
      const handleGlobalMouseMove = (e: MouseEvent) => {
        if (globalCursorEl) {
          globalCursorEl.style.left = `${e.clientX}px`;
          globalCursorEl.style.top = `${e.clientY}px`;
          globalCursorEl.style.display = 'block';
        }
      };

      // Show cursor immediately at current mouse position
      const showCursorAtCurrentPosition = () => {
        if (globalCursorEl && document.body) {
          // Try to get current mouse position from last known event
          const rect = document.body.getBoundingClientRect();
          globalCursorEl.style.left = `${rect.width / 2}px`;
          globalCursorEl.style.top = `${rect.height / 2}px`;
          globalCursorEl.style.display = 'block';
        }
      };

      document.addEventListener('mousemove', handleGlobalMouseMove);
      showCursorAtCurrentPosition();

      // Handle ESC key to cancel icon selection
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && onIconSelectCancel) {
          onIconSelectCancel();
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      // Store cleanup function
      const cleanup = () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.cursor = '';
        if (container) {
          container.style.cursor = '';
        }
      };

      (globalCursorEl as any).__cleanup = cleanup;
    };

    updateIconMode();

    // Cleanup
    return () => {
      if (map.current) {
        map.current.dragPan.enable();
        const container = map.current.getCanvasContainer();
        if (container) {
          container.style.cursor = '';
        }
      }
      if (globalCursorElementRef.current) {
        const cleanup = (globalCursorElementRef.current as any).__cleanup;
        if (cleanup) cleanup();
        globalCursorElementRef.current.remove();
        globalCursorElementRef.current = null;
      }
      document.body.style.cursor = '';
    };
  }, [selectedIconId, mapScale, isInitialized]);



  // Render map icons - optimalizováno pro rychlou aktualizaci barev
  // Použít useCallback pro updateMarkers funkci
  const updateMarkers = useCallback(async () => {
    if (!map.current || !isInitialized) {
      return;
    }

    // if (import.meta.env.DEV) {
    //   console.log(`[MapCanvas] Rendering ${mapIcons.length} icons`);
    // }

    const existingIconIds = new Set(markersMapRef.current.keys());
    const currentIconIds = new Set(mapIcons.map(icon => icon.id));

    // Odstranit markery pro ikony, které už neexistují
    for (const iconId of existingIconIds) {
      if (!currentIconIds.has(iconId)) {
        const marker = markersMapRef.current.get(iconId);
        if (marker) {
          marker.remove();
          markersMapRef.current.delete(iconId);
          // if (import.meta.env.DEV) {
          //   console.log(`[MapCanvas] Removed marker for icon ${iconId}`);
          // }
        }
      }
    }

    // Aktualizovat nebo vytvořit markery pro všechny ikony
    for (const icon of mapIcons) {
      try {
        const existingMarker = markersMapRef.current.get(icon.id);

        if (existingMarker && existingMarker.getElement()) {
          // Marker existuje a má element - aktualizovat
          // Aktualizovat existující marker - pouze barvu a další vlastnosti
          const element = existingMarker.getElement();
          if (element) {
            const iconData = await iconDataCache.get(icon.iconId);
            if (iconData) {
              // Aktualizovat barvu ikony - OKAMŽITĚ a SYNCHRONNĚ pomocí SVG fill
              const svgElement = element.querySelector('svg') as SVGSVGElement;
              if (svgElement) {
                const iconColor = getIconColor(icon, iconData);

                // if (import.meta.env.DEV) {
                //   console.log(`[MapCanvas] Updating icon ${icon.id}: icon.color=${icon.color}, iconColor=${iconColor}`);
                // }
                const svgContent = await svgIconCache.getSvg(iconData.storage_url);
                if (svgContent) {
                  const coloredSvg = updateSvgFill(svgContent, iconColor);
                  const sanitizedSvg = sanitizeSvg(coloredSvg);

                  // Použít DOMParser pro správné parsování SVG
                  try {
                    const parser = new DOMParser();
                    const svgDoc = parser.parseFromString(sanitizedSvg, 'image/svg+xml');

                    const parserError = svgDoc.querySelector('parsererror');
                    if (parserError) {
                      console.warn(`[MapCanvas] SVG parsing error for icon ${icon.id}:`, parserError.textContent);
                    } else {
                      const newSvgElement = svgDoc.querySelector('svg') as SVGSVGElement;
                      if (newSvgElement) {
                        newSvgElement.style.width = '100%';
                        newSvgElement.style.height = '100%';
                        newSvgElement.style.display = 'block';
                        newSvgElement.style.margin = '0';
                        newSvgElement.style.padding = '0';
                        newSvgElement.style.verticalAlign = 'top';
                        if (iconData.name) {
                          newSvgElement.setAttribute('aria-label', iconData.name);
                        }
                        // Nahradit starý SVG novým
                        svgElement.replaceWith(newSvgElement);

                        // if (import.meta.env.DEV) {
                        //   console.log(`[MapCanvas] ✅ Updated icon ${icon.id}: color=${iconColor}, SVG fill updated`);
                        // }
                      }
                    }
                  } catch (err) {
                    console.error(`[MapCanvas] Error parsing SVG for icon ${icon.id}:`, err);
                  }
                }
              } else {
                if (import.meta.env.DEV) {
                  console.warn(`[MapCanvas] ⚠️ No SVG element found for icon ${icon.id}`);
                }
              }

              // Aktualizovat velikost - PŘED aktualizací pozice, aby se anchor point správně aplikoval
              const newWidth = `${icon.width || iconData.default_width || 40}px`;
              const newHeight = `${icon.height || iconData.default_height || 40}px`;
              const currentWidth = element.style.width;
              const currentHeight = element.style.height;

              // Aktualizovat velikost pouze pokud se změnila
              if (currentWidth !== newWidth || currentHeight !== newHeight) {
                element.style.width = newWidth;
                element.style.height = newHeight;
              }

              // Aktualizovat draggable state
              existingMarker.setDraggable(selectedMapIconId === icon.id);

              // Aktualizovat highlight
              if (selectedMapIconId === icon.id) {
                element.style.outline = '2px solid #3b82f6';
                element.style.outlineOffset = '2px';
                element.style.borderRadius = '4px';
              } else {
                element.style.outline = '';
                element.style.outlineOffset = '';
                element.style.borderRadius = '';
              }

              // Aktualizovat pozici POUZE pokud se skutečně změnila
              // Získat aktuální pozici markeru
              const currentLngLat = existingMarker.getLngLat();
              const latChanged = Math.abs(currentLngLat.lat - icon.lat) > 0.000001; // Tolerance pro floating point
              const lngChanged = Math.abs(currentLngLat.lng - icon.lng) > 0.000001;

              if (latChanged || lngChanged) {
                existingMarker.setLngLat([icon.lng, icon.lat]);
                if (import.meta.env.DEV) {
                  console.log(`[MapCanvas] Position updated for icon ${icon.id}: [${icon.lng}, ${icon.lat}]`);
                }
              }

              // Aktualizovat label barvu pokud existuje
              const label = element.querySelector('div[style*="position: absolute"]') as HTMLElement;
              if (label && icon.label) {
                const labelColor = getIconColor(icon, iconData);
                label.style.color = labelColor || '#000000';
              }
            }
          }
        } else {
          // Vytvořit nový marker
          const iconData = await iconDataCache.get(icon.iconId);
          if (!iconData) {
            console.warn(`Failed to load icon ${icon.iconId}`);
            continue;
          }

          // Create custom HTML element for the icon
          const el = document.createElement('div');
          el.className = 'map-icon-marker';
          el.style.width = `${icon.width || iconData.default_width || 40}px`;
          el.style.height = `${icon.height || iconData.default_height || 40}px`;
          el.style.cursor = selectedMapIconId === icon.id ? 'move' : 'pointer';
          el.style.position = 'relative';
          el.style.margin = '0';
          el.style.padding = '0';
          el.style.lineHeight = '0'; // Zajistit, že inline elementy nemají extra výšku
          el.style.overflow = 'visible';

          // Add click handler
          el.addEventListener('click', (e) => {
            e.stopPropagation();
            if (onIconClick) {
              onIconClick(icon.id);
            }
          });

          // Highlight if selected
          if (selectedMapIconId === icon.id) {
            el.style.outline = '2px solid #3b82f6';
            el.style.outlineOffset = '2px';
            el.style.borderRadius = '4px';
          }

          // Použij jednotnou logiku pro určení barvy ikony - default je "black"
          const iconColor = getIconColor(icon, iconData);

          if (import.meta.env.DEV) {
            console.log(`[MapCanvas] Creating new icon ${icon.id}: icon.color=${icon.color}, iconColor=${iconColor}, iconData.default_color=${iconData.default_color}`);
          }

          // Načíst SVG a upravit fill atribut
          const svgContent = await svgIconCache.getSvg(iconData.storage_url);
          if (svgContent) {
            const coloredSvg = updateSvgFill(svgContent, iconColor);
            const sanitizedSvg = sanitizeSvg(coloredSvg);

            // Použít DOMParser pro správné parsování SVG
            try {
              const parser = new DOMParser();
              const svgDoc = parser.parseFromString(sanitizedSvg, 'image/svg+xml');

              // Zkontrolovat, zda parsování bylo úspěšné
              const parserError = svgDoc.querySelector('parsererror');
              if (parserError) {
                console.warn(`[MapCanvas] SVG parsing error for icon ${icon.id}:`, parserError.textContent);
              } else {
                const svgElement = svgDoc.querySelector('svg') as SVGSVGElement;
                if (svgElement) {
                  // Nastavit styly pro správné zobrazení - podobně jako img tag měl objectFit: contain
                  // Použít inline-block místo block pro lepší zarovnání
                  svgElement.style.width = '100%';
                  svgElement.style.height = '100%';
                  svgElement.style.display = 'block';
                  svgElement.style.margin = '0';
                  svgElement.style.padding = '0';
                  svgElement.style.verticalAlign = 'top';
                  svgElement.style.boxSizing = 'border-box';
                  // Přidat alt text jako title pro přístupnost
                  if (iconData.name) {
                    svgElement.setAttribute('aria-label', iconData.name);
                  }
                  el.appendChild(svgElement);

                  if (import.meta.env.DEV) {
                    console.log(`[MapCanvas] Icon ${icon.id}: icon.color=${icon.color}, iconColor=${iconColor}, SVG loaded`);
                  }
                } else {
                  console.warn(`[MapCanvas] No SVG element found in parsed content for icon ${icon.id}`);
                }
              }
            } catch (err) {
              console.error(`[MapCanvas] Error parsing SVG for icon ${icon.id}:`, err);
            }
          } else {
            console.warn(`[MapCanvas] Failed to load SVG for icon ${icon.id}`);
          }

          // Add label if present
          if (icon.label) {
            const label = document.createElement('div');
            label.textContent = icon.label;
            label.style.position = 'absolute';
            label.style.bottom = '-20px';
            label.style.left = '50%';
            label.style.transform = 'translateX(-50%)';
            label.style.whiteSpace = 'nowrap';
            label.style.fontSize = '12px';
            // Barva labelu - použij barvu ikony (vždy vrátí validní barvu)
            const labelColor = getIconColor(icon, iconData);
            label.style.color = labelColor;
            label.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
            label.style.padding = '2px 6px';
            label.style.borderRadius = '4px';
            el.appendChild(label);
          }

          // Create marker
          const marker = new mapboxgl.Marker({
            element: el,
            anchor: 'center',
            draggable: selectedMapIconId === icon.id, // Enable drag only for selected icon
          })
            .setLngLat([icon.lng, icon.lat]);

          // Handle drag end to update icon position
          marker.on('dragend', () => {
            const lngLat = marker.getLngLat();
            if (onIconUpdate) {
              onIconUpdate(icon.id, lngLat.lat, lngLat.lng);
            }
          });

          if (map.current) {
            marker.addTo(map.current);
            markersMapRef.current.set(icon.id, marker);
            if (import.meta.env.DEV) {
              console.log(`[MapCanvas] ✅ Marker created and stored for icon ${icon.id}, total markers: ${markersMapRef.current.size}`);
            }
          }
        }
      } catch (error) {
        console.error(`Error updating marker for icon ${icon.id}:`, error);
      }
    }

    // Aktualizovat markersRef pro kompatibilitu
    markersRef.current = Array.from(markersMapRef.current.values());
  }, [mapIcons, selectedMapIconId, isInitialized, onIconClick, onIconUpdate]);

  // useEffect pro spuštění updateMarkers
  useEffect(() => {
    updateMarkers();

    // Cleanup
    return () => {
      markersMapRef.current.forEach((marker) => marker.remove());
      markersMapRef.current.clear();
      markersRef.current = [];
    };
  }, [updateMarkers]);

  // Tento useEffect je nyní integrován do hlavního useEffect pro renderování ikon
  // pro lepší výkon a okamžitou aktualizaci barev

  return (
    <>
      <div
        id="map-container"
        className="absolute inset-0 z-0 map-container"
        style={{
          width: `${width}px`,
          height: `${height}px`,
          overflow: 'hidden',
        }}
      >
        <div
          id="mapbox-container"
          ref={mapContainer}
          className="w-full h-full"
          style={{
            width: `${width}px`,
            height: `${height}px`,
            minWidth: `${width}px`,
            minHeight: `${height}px`,
            maxWidth: `${width}px`,
            maxHeight: `${height}px`,
            backgroundColor: '#f0f8ff', // Fallback pozadí pro případ, kdy se mapa nevykreslí
          }}
        />
        {!isInitialized && (
          <div id="map-loading-overlay" className="absolute inset-0 flex items-center justify-center bg-gray-100 pointer-events-none">
            <div className="text-center">
              <div className="text-sm text-muted-foreground mb-2">{t('settings.loadingMap')}</div>
            </div>
          </div>
        )}
      </div>

      {/* Textové elementy */}
      {textElements.length > 0 && (
        <div
          id="map-text-elements-container"
          className="absolute inset-0 z-10 pointer-events-none"
          style={{
            width: `${width}px`,
            height: `${height}px`,
          }}
        >
          <MapTextElements
            textElements={textElements}
            width={width}
            height={height}
            textData={textData}
          />
        </div>
      )}

      {/* Lock/Unlock button */}
      {isInitialized && (
        <button
          onClick={() => setIsMapLocked(!isMapLocked)}
          className={`absolute top-4 right-4 z-50 rounded-lg p-2.5 shadow-lg transition-all ${isMapLocked
            ? 'bg-blue-500/90 hover:bg-blue-600/90 text-white'
            : 'bg-white/90 backdrop-blur-sm hover:bg-white text-gray-700'
            }`}
          title={isMapLocked ? t('settings.unlockMap') : t('settings.lockMap')}
          style={{ pointerEvents: 'auto' }}
        >
          {isMapLocked ? (
            <Lock className="w-5 h-5" />
          ) : (
            <Unlock className="w-5 h-5" />
          )}
        </button>
      )}

      {/* Debug Panel removed - moved to workspace */}
    </>
  );
}
